import LocalLink from "@/components/LocalLink";
import { Helmet } from "react-helmet-async";
import { useCopy } from "@/lib/i18n/copy";
import { BREADCRUMBS } from "@/content/chrome";

interface Crumb {
  label: string;
  href?: string;
}

const BASE_URL = "https://eventboutique.pt";

const Breadcrumbs = ({ items }: { items: Crumb[] }) => {
  const { t } = useCopy(BREADCRUMBS);
  // Google requires an `item` (URL) on every BreadcrumbList element except the last one.
  // A middle crumb without `href` produces "Missing field 'item' (in itemListElement)" in
  // Search Console and drops the breadcrumb rich result (hit on three service pages, Aug 2026).
  // Only the final crumb, the current page, may omit href.
  if (import.meta.env.DEV) {
    items.slice(0, -1).forEach((crumb) => {
      if (!crumb.href) {
        console.warn(
          `[Breadcrumbs] Crumb "${crumb.label}" is not the last item but has no href. ` +
            `Google will reject this BreadcrumbList. Add an href.`,
        );
      }
    });
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      ...items.map((item, i) => ({
        "@type": "ListItem" as const,
        position: i + 2,
        name: item.label,
        ...(item.href
          ? { item: `${BASE_URL}${item.href}` }
          : {}),
      })),
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <nav aria-label={t.ariaLabel} className="text-caption text-muted-foreground mb-8">
        <ol className="flex flex-wrap gap-1 items-center">
          <li><LocalLink to="/" className="hover:text-foreground transition-colors">{t.home}</LocalLink></li>
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-1">
              <span className="mx-1">›</span>
              {item.href ? (
                <LocalLink to={item.href} className="hover:text-foreground transition-colors">{item.label}</LocalLink>
              ) : (
                <span className="text-foreground">{item.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
